import { useEffect, useState } from 'react';
import { Button, Chip, CircularProgress, Divider, Grid, Link, Paper, Typography } from '@mui/material';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Page from '../components/Controls/Page';
import { BaseAPI } from '../utils/Api';
import Notification from '../utils/Notification';
import { getItem } from '../utils/Helper';
import LoadingState from '../components/Controls/LoadingState';
import { FormProvider, RHFTextField } from '../components/hook-form';
import { ReplySchema } from '../utils/SchemaValidation';
import EmptyState from '../components/Controls/EmptyState';

const TicketDetails = () => {
  const { id } = useParams();

  const [fileName, setFileName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [ticketInformation, setTicketInformation] = useState({});
  const [invalidTicket, setInvalidTicket] = useState(false);

  const defaultValues = {
    details: '',
  };

  const methods = useForm({
    resolver: yupResolver(ReplySchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    getTicketDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTicketDetails = () => {
    setLoading(true);
    BaseAPI.get(`/ticket/user/details/${id}`, { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
      .then((res) => {
        console.log(res);
        setTicketInformation(res.data.data);
      })
      .catch((err) => {
        if (err?.response?.data?.message === 'Ticket not found!') {
          setInvalidTicket(true);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleFileUploadChange = (e) => {
    setLoading(true);
    setFileName(e.target.files[0].name);
    let formData = new FormData();

    formData.append('attach_file', e.target.files[0]);

    BaseAPI.post('/upload/files/public', formData)
      .then((res) => {
        setFile(res.data.data.url);
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => setLoading(false));
  };

  const onSubmit = async (data) => {
    const body = {
      ticket_id: id,
      details: data.details,
    };

    if (file) {
      body.attachment = file;
    }
    BaseAPI.post('/ticket/reply', body, { headers: { Authorization: `Bearer ${getItem(localStorage, 'at')}` } })
      .then(() => {
        Notification('Reply sent', 'success');
        getTicketDetails();
        reset();
        setFileName('');
      })
      .catch((err) => {
        Notification(err?.response?.data?.message, 'error');
      });
  };

  return (
    <Page title='Ticket Details'>
      <LoadingState loading={loading} />
      {invalidTicket ? (
        <EmptyState message="Your requested ticket can't be found! Please try a valid ticket" goToLink='/my-tickets' goToText='Go to my ticket' />
      ) : (
        <>
          <Typography variant='h4' mt={2} mb={4} className='bold'>
            Ticket Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Typography variant='subtitle1' className='bold'>
                Ticket ID
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant='subtitle1'>{ticketInformation.ticket_id}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='subtitle1' className='bold'>
                Issue Type
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant='subtitle1'>{ticketInformation.issue_type}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='subtitle1' className='bold'>
                Priority
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant='subtitle1'>{ticketInformation.priority}</Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant='subtitle1' className='bold'>
                Status
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Typography variant='subtitle1'>
                <Chip label={ticketInformation.is_resolved ? 'Resolved' : 'Open'} color={ticketInformation.is_resolved ? 'success' : 'error'} />
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant='subtitle2' mt={2} mb={4}>
            Type a reply
          </Typography>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RHFTextField name='details' label='Message' multiline rows={4} />
              </Grid>
              <Grid item xs={12}>
                <Button component='label' color='secondary' startIcon={<AttachFileRoundedIcon />}>
                  {fileName ? fileName : 'Attach File'}
                  <input hidden accept='image/*' multiple type='file' onChange={handleFileUploadChange} />
                </Button>
              </Grid>
              <Grid item xs={12}>
                <Button type='submit' variant='contained' disableElevation color='primary' disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} color='primary' /> : 'Send Reply'}
                </Button>
              </Grid>
            </Grid>
          </FormProvider>

          <Grid container spacing={2} mt={4} sx={{ maxHeight: '30em', overflow: 'scroll' }}>
            {ticketInformation.replies?.map((reply, i) => (
              <Grid item xs={12} key={i}>
                <Paper sx={{ p: 2, border: `1px solid ${reply.replied_info.f_name === 'Data' ? '#1d9e5e' : 'orange'}`, backgroundColor: '#ECECEC' }} variant='outlined'>
                  <Typography variant='h6' mb={1}>
                    {reply.replied_info.f_name}&nbsp;{reply.replied_info.l_name}
                  </Typography>
                  <Typography variant='subtitle2' mb={2}>
                    {moment(reply.createdAt).format('lll')}
                  </Typography>
                  <Typography variant='body1' mb={1}>
                    {reply.details}
                  </Typography>

                  <Link href={`${process.env.REACT_APP_s3_cdn}/${process.env.REACT_APP_STAGE}/attach_file/${reply.attachment}`} target='_blank'>
                    {reply.attachment}
                  </Link>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Page>
  );
};

export default TicketDetails;
